<template>
    <div>
        <div class="page-content px-4 top">
            <template-breadcrumb v-if="isDataLoaded" :title="details.name" />
            <template-header v-if="isDataLoaded" :title="details.name" />

            <b-card footer-class="p-0" class="rounded-lg" :key="cardKey">
                <div class="profile-details">
                    <input type="hidden" name="id" :value="templateId" />

                    <loader-profile
                        v-if="!isDataLoaded"
                        :show-image="false"
                    ></loader-profile>

                    <b-row v-else-if="isDataLoaded">
                        <b-col lg="6">
                            <b-form-group
                                label-for="name"
                                label-cols="3"
                                label="Name:"
                            >
                                <div v-if="isEditMode">
                                    <b-input
                                        id="name"
                                        v-model="editDetails.name"
                                        :class="{
                                            'border-danger':
                                                errors.has('name') ||
                                                isNameExceed
                                        }"
                                        v-validate="'required'"
                                        name="name"
                                        type="text"
                                        placeholder="Name (required)"
                                        :disabled="isSavingEdit"
                                    />
                                    <span
                                        v-bind:style="[
                                            nameCount >= 0
                                                ? { color: 'grey' }
                                                : { color: 'red' }
                                        ]"
                                        style="font-size: 10px;"
                                        class="mt-1 d-block text-right"
                                    >
                                        {{ nameCount }} character(s) remaining
                                    </span>
                                </div>
                                <span v-else class="d-block pt-2">{{
                                    details.name
                                }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="description"
                                label-cols="3"
                                label="Description:"
                            >
                                <b-textarea
                                    v-if="isEditMode"
                                    v-model="editDetails.description"
                                    name="description"
                                    placeholder="Description (optional)"
                                    :disabled="isSavingEdit"
                                />

                                <span v-else class="d-block pt-2">
                                    {{ details.description }}
                                </span>
                            </b-form-group>
                        </b-col>
                        <b-col lg="6">
                            <b-form-group
                                label-for="create_at"
                                label-cols="3"
                                label="Created:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        this._carbonDateDisplay(
                                            this.details.create_at
                                        )
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="create_by"
                                label-cols="3"
                                label="Created By:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        this.details.create_by
                                            ? this.details.create_by.label
                                            : null
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="updated"
                                label-cols="3"
                                label="Updated:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        this._carbonDateDisplay(
                                            this.details.update_at
                                        )
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="last_used"
                                label-cols="3"
                                label="Last Used:"
                            >
                                <b-input
                                    v-if="details.last_used"
                                    plaintext
                                    :value="
                                        this._setDateDisplay(
                                            this.details.last_used
                                        )
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="total_used"
                                label-cols="3"
                                label="Total Use:"
                            >
                                <b-input
                                    plaintext
                                    :value="details.total_used"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
            </b-card>

            <!-- WEBFORM survey.js -->
            <!-- can add button to view in fullscreen perhaps -->
            <b-card
                v-if="templateId"
                body-class="p-0"
                footer-class="p-0"
                class="rounded-lg mt-5"
            >
                <!-- For smooth transition loading to surveyJS element -->
                <loader-profile
                    v-if="!isSurveyJSLoaded"
                    :is-full="true"
                    class="p-2"
                ></loader-profile>
                <SurveyCreator
                    :webform="details"
                    :webformContent="webformContent"
                    module="setting"
                    @setSurveyDirty="isDirty => (isSurveyDirty = isDirty)"
                    @setWebformContent="data => (webformContent = data)"
                    @loaded="isLoaded => (isSurveyJSLoaded = isLoaded)"
                />
            </b-card>
        </div>
    </div>
</template>

<script>
import { isEmpty } from "lodash";
import TEMPLATES from "@/constants/templates";
import TemplateService from "@/services/templates";
import SettingTemplateMixin from "@/mixins/settings-template";
const DEFAULT_DATA = {
    name: null,
    description: null,
    create_at: null,
    update_at: null,
    last_used: null,
    total_used: null
};

export default {
    mixins: [SettingTemplateMixin],
    components: {
        LoaderProfile: () => import("@/components/elements/loader-profile.vue"),
        TemplateBreadcrumb: () =>
            import("@/module/settings/template/breadcrumb.vue"),
        TemplateHeader: () => import("@/module/settings/template/header.vue"),
        SurveyCreator: () =>
            import("@/components/setting-webform/survey-creator.vue")
    },
    data() {
        return {
            isDataLoaded: false,
            templateId: null,
            isNameExceed: false,
            nameCount: 100,
            isSurveyJSLoaded: false,
            cardKey: 0,
            isWebformDirty: false,
            isSurveyDirty: false
        };
    },
    computed: {
        details: {
            get() {
                return this.$store.state.settingsTemplate.details;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setDetails", value);
            }
        },
        webformContent: {
            get() {
                return this.$store.state.settingsTemplate.webformContent;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setWebformContent", value);
            }
        },
        editDetails: {
            get() {
                return this.$store.state.settingsTemplate.editDetails;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setEditDetails", value);
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.settingsTemplate.isEditMode;
            }
        },
        isSavingEdit() {
            return this.$store.state.settingsTemplate.isSavingEdit;
        },
        items: {
            get() {
                return this.$store.state.settingsTemplate.items;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setItems", value);
            }
        },
        breadcrumbs() {
            let breadcrumbs = [
                {
                    text: "Settings",
                    href: process.env.APPS + "business/business"
                },
                {
                    text: "List of Form Templates",
                    to: { name: "template_list", params: { type: "form" } }
                }
            ];
            if (this.details && this.details.name) {
                breadcrumbs.push({
                    text:
                        this.details.name.length > 60
                            ? this.details.name.substring(0, 60 - 3) + "..."
                            : this.details.name,
                    active: true
                });
            }
            return breadcrumbs;
        },
        routeParam() {
            return this.$route.params.id;
        },
        form_dirty() {
            return (
                this.isWebformDirty ||
                this.isSurveyDirty ||
                (!isEmpty(this.editDetails) &&
                    this.editDetails.description != this.details.description)
            );
        }
    },
    watch: {
        form_dirty(value) {
            this.$emit("setFormDirty", value);
        },
        fields: {
            handler() {
                this.isWebformDirty = this.is_dirty();
            },
            deep: true
        },
        "editDetails.name"(value) {
            let valueLength = 0;
            if (value) {
                valueLength = value.length;
            }
            this.nameCount = 100 - valueLength;

            if (this.nameCount < 0) {
                this.isNameExceed = true;
            } else {
                this.isNameExceed = false;
            }
        },
        routeParam() {
            this.fetchData();
            this.cardKey++;
        }
    },
    mounted() {
        this.fetchData();

        this.$store.commit(
            "settingsTemplate/setTemplateType",
            TEMPLATES.TYPE.WEBFORM_TEMPLATE
        );
    },
    beforeDestroy() {
        // to make sure the button in display mode when re-open webform
        this.$store.commit("settingsTemplate/toggleEdit", false);
    },
    methods: {
        async fetchData() {
            try {
                this.details = DEFAULT_DATA;
                this.editDetails = DEFAULT_DATA;
                this.templateId = null;
                this.$store.commit("settingsTemplate/setTemplateId", null); // prettier-ignore

                this.isDataLoaded = false;

                let encodedTemplateId = this.$route.params.id;

                // fetch details
                const fetchDetails = await TemplateService.get(
                    encodedTemplateId
                );

                this.validateTemplateType(
                    fetchDetails.type,
                    TEMPLATES.TYPE.WEBFORM_TEMPLATE
                );

                this.appendData(fetchDetails);
            } catch (error) {
                console.error(error);
                this.isDataLoaded = true;
                this._message(error.message, "danger");
            }
        },
        appendData(details) {
            this.details = details;
            this.editDetails = details;
            this.webformContent = details.webform_content;

            this.templateId = this.details.id; // plain id
            this.isDataLoaded = true;

            // store templateId in vuex
            this.dynamicPageTitle(this.details.name);
            this.storeAudit();
            this.$store.commit("settingsTemplate/setTemplateId", this.templateId); // prettier-ignore
        },
        async storeAudit() {
            const templateId = _.clone(this.templateId);
            const fullPath = _.clone(this.$router.currentRoute.fullPath);

            if (!templateId) {
                return;
            }

            await this._getSetting();
            await this._saveAudit({
                action: "view",
                path: "/business/template/view." + templateId,
                reference_id: templateId,
                fullPath
            });
        },
        async save() {
            let valid = await this.$validator.validateAll();
            if (!valid || this.isNameExceed) {
                return false;
            }

            let response = await TemplateService.put(this.templateId, {
                name: this.editDetails.name,
                description: this.editDetails.description
            });

            if (response) {
                this.details = response;
                this._message("Edits successfully saved.");
            }
            this.$store.commit("settingsTemplate/toggleEdit", false);
            this.$store.commit("settingsTemplate/toggleSavingEdit", false);
        },
        is_dirty() {
            return Object.keys(this.fields).some(key => this.fields[key].dirty);
        }
    }
};
</script>
<style scoped>
.top {
    margin-top: 10px;
    margin-bottom: 3px;
}
</style>
